import { useEffect, useRef, useState } from "react";
import { DEBUG } from "../../lib/debug.js";
import {
  BOARD_SIZE,
  TICKS_PER_SECOND,
  TOWER_KINDS,
  defenceBudget,
  idx,
  towerSlots,
  towerStats,
  validateLayout,
  type Layout,
  type TowerKind,
} from "@empire/core-sim";
import { BoardNotice } from "../components/BoardNotice.js";
import { BoardView, type RangeRing } from "../../pixi/BoardView.js";
import { PRESETS, kindsUsed, planCost, planSize } from "../../sim/presets.js";
import { budgetLines, towerUnavailable, useEditorStore, type Brush } from "../../store/editor.js";
import { SPEEDS, useTestStore } from "../../store/testRun.js";
import { SaveBanner, SaveButton, UpgradeBill, useUnsavedGuard } from "../components/SaveBar.js";
import { UnitMark } from "../components/UnitMark.js";
import { ResourceIcon } from "../components/ResourceIcon.js";
import {
  MAX_TOWER_LEVEL,
  RESOURCE_KINDS,
  defaultLayout,
  towerLevelBlock,
  upgradeBill,
  upgradeCost,
} from "@empire/shared";
import { useShallow } from "zustand/react/shallow";
import { useSaveStore } from "../../store/save.js";
import { predictedResources, useVillageStore } from "../../store/village.js";
import { TOWER_BLURB, unitKindsFromPlan } from "../theme/glyphs.js";

/**
 * S07, the defence editor — the screen the whole game is built around.
 *
 * The defender draws the road; everything else reacts. Validation runs on every
 * stroke using core-sim's `validateLayout`, so the message under the board is
 * literally what the server would say, and the offending tiles pulse on the
 * board rather than being described in words.
 */

/**
 * Five tools, not seven.
 *
 * Mud and Barricade used to have their own buttons. On a 390px phone that made
 * the row 525px wide, so two tools sat off the edge of the screen — and they
 * were not needed, because S2-3 already lets you hold a road tile to cycle it
 * road -> mud -> barricade in place. Both of those surfaces sit ON the road
 * anyway, so painting them as a separate brush was always the long way round.
 */
const TOOLS: { brush: Brush; label: string; hint: string }[] = [
  {
    brush: "road",
    label: "Road",
    hint: "The path attackers walk. Hold a tile to make it mud or a barricade.",
  },
  { brush: "gate", label: "Gate", hint: "Where they enter — edge only" },
  { brush: "select", label: "Towers", hint: "Build, inspect and move towers" },
  { brush: "erase", label: "Erase", hint: "Clear a tile" },
];

/** What the board should draw for the selected slot, or null. */
function ringFor(kind: TowerKind | undefined): RangeRing | null {
  if (!kind) return null;
  const stats = towerStats(kind, 1);
  return { range: stats.rangeMilli / 1000, minRange: stats.minRangeMilli / 1000 };
}

export function DefenceEditor({ onBack }: { onBack?: () => void } = {}): JSX.Element {
  const hostRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<BoardView | null>(null);
  const [fps, setFps] = useState(0);
  const [drawables, setDrawables] = useState(0);
  const [zoom, setZoom] = useState(1);
  const framed = useRef(false);

  const layout = useEditorStore((s) => s.layout);
  const validation = useEditorStore((s) => s.validation);
  const brush = useEditorStore((s) => s.brush);
  const selected = useEditorStore((s) => s.selected);
  const pending = useEditorStore((s) => s.pending);
  const towerBrush = useEditorStore((s) => s.towerBrush);
  const stroke = useEditorStore((s) => s.stroke);
  const setBrush = useEditorStore((s) => s.setBrush);

  // Warn before a closing tab takes an unsaved maze with it.
  useUnsavedGuard();

  // A village is never without a legal defence (29 Sep 2026). The server
  // only ever stores legal ones; this makes the editor agree when the
  // defender walks away from an empty or half-drawn board (after Clear, say):
  // it goes back to the defence that is actually standing — their last saved
  // one, which for anyone who never saved is the default road.
  useEffect(
    () => () => {
      const { layout } = useEditorStore.getState();
      if (validateLayout(layout).ok) return;
      const saved =
        useSaveStore.getState().savedLayout ??
        defaultLayout({ wallLevel: layout.wallLevel, engineersGuildLevel: layout.engineersGuildLevel });
      useEditorStore.getState().load({
        ...saved,
        wallLevel: layout.wallLevel,
        engineersGuildLevel: layout.engineersGuildLevel,
      });
    },
    []
  );

  // Mount the Pixi view once. It is deliberately not re-created on every edit.
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    let cancelled = false;
    let view: BoardView | undefined;
    let frames = 0;
    let last = performance.now();
    let lastPlaying: string | null | undefined;

    // Straight from above: square tiles a finger can hit, rows that run along
    // the screen (playtest, 29 Sep 2026).
    void BoardView.mount(host, useEditorStore.getState().layout, { topDown: true }).then((mounted) => {
      if (cancelled) {
        mounted.destroy();
        return;
      }
      view = mounted;
      viewRef.current = mounted;

      mounted.onViewChange(setZoom);
      // Always open at finger-sized tiles — on an empty board too, centred on
      // the gate and the core. The saved maze re-frames once when it lands.
      frameEditor(mounted, useEditorStore.getState().layout);
      if (hasMaze(useEditorStore.getState().layout)) framed.current = true;
      mounted.on({
        onPaint: (at) => useEditorStore.getState().paint(at),
        onPaintEnd: () => useEditorStore.getState().commit(),
        onLongPress: (at) => useEditorStore.getState().longPress(at),
      });

      mounted.app.ticker.add((ticker) => {
        // Drive the mini replay from the render loop, so playback speed is tied
        // to real time rather than to how often React happens to re-render.
        const playing = useTestStore.getState().playing;
        if (playing !== lastPlaying) {
          if (playing) mounted.frameAction();
          else if (lastPlaying) frameEditor(mounted, useEditorStore.getState().layout);
          // Each preset sends a different army, so the shapes on the board have
          // to change with it. Done on change rather than per frame — the
          // mapping is the same for every tick of one run.
          lastPlaying = playing;
          const plan = PRESETS.find((p) => p.id === playing)?.plan;
          mounted.setUnitKinds(plan ? unitKindsFromPlan(plan) : []);
        }
        useTestStore.getState().advance(ticker.deltaMS);
        const test = useTestStore.getState();
        const run = test.runs.find((r) => r.presetId === test.playing);
        mounted.setReplay(run ? run.frames : null, test.position, !!test.playing && !test.paused);

        frames += 1;
        const now = performance.now();
        if (now - last >= 500) {
          setFps(Math.round((frames * 1000) / (now - last)));
          setDrawables(mounted.drawableCount);
          frames = 0;
          last = now;
        }
      });
    });

    return () => {
      cancelled = true;
      viewRef.current = null;
      view?.destroy();
    };
  }, []);

  // Push every edit into the view. Cheap: it redraws one Graphics plus the
  // handful of raised blocks.
  useEffect(() => {
    const view = viewRef.current;
    view?.setLayout(layout, validation.ok ? [] : validation.error.tiles);
    // The saved maze usually arrives after the board mounts: zoom to it once,
    // when it first appears, and never again behind the defender's back.
    if (view && !framed.current && hasMaze(layout)) {
      frameEditor(view, layout);
      framed.current = true;
    }
  }, [layout, validation]);

  // A result that describes a maze you have since edited is worse than no
  // result, so editing throws the old answers away rather than leaving them
  // on screen looking current.
  useEffect(() => {
    useTestStore.getState().invalidate();
  }, [layout]);

  useEffect(() => {
    // While an armed tower is being placed, preview it: the outline where it
    // lands and the ground it would cover.
    if (pending) {
      viewRef.current?.setPending(pending);
      viewRef.current?.setSelection(pending, ringFor(towerBrush ?? movingKind(layout, stroke)));
      return;
    }
    viewRef.current?.setPending(null);
    const tile = selected ? layout.tiles[idx(selected.x, selected.y, layout.size)] : undefined;
    viewRef.current?.setSelection(selected, ringFor(tile?.tower?.kind));
  }, [selected, layout, pending, towerBrush, stroke]);

  // Keyboard undo/redo, because the editor is used on desktop while designing.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (!(event.ctrlKey || event.metaKey)) return;
      if (event.key.toLowerCase() !== "z") return;
      event.preventDefault();
      if (event.shiftKey) useEditorStore.getState().redo();
      else useEditorStore.getState().undo();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="flex h-full flex-col">
      <Toolbar brush={brush} onPick={setBrush} onBack={onBack} />

      {/* overflow-hidden is load-bearing: Pixi sizes its canvas from the host
          element and can lag a layout change by a frame, and a canvas even a
          few pixels too tall paints straight over the validation message. */}
      <div className="relative min-h-0 flex-1 overflow-hidden">
        <div
          ref={hostRef}
          className="h-full w-full touch-none [&>canvas]:block"
          data-testid="board-canvas"
        />
        {DEBUG ? (
          <div className="pointer-events-none absolute left-3 top-3 rounded bg-black/45 px-2 py-1 text-[11px] text-ink-dim">
            {BOARD_SIZE}×{BOARD_SIZE} · {fps} fps · {drawables} drawables
          </div>
        ) : null}
        <BoardNotice />
        <HistoryControls />
        <ZoomControls view={viewRef} zoom={zoom} />
        <Inspector />
      </div>

      <TestPanel />
      <Status />
    </div>
  );
}

/**
 * The tool row.
 *
 * Undo, redo and clear used to live at the right-hand end of this row. On a
 * 390px phone that put them off the edge of the screen entirely, reachable only
 * by a sideways scroll nothing advertised — so they now float over the board
 * where they are always visible. (S2-7)
 */
function Toolbar({
  brush,
  onPick,
  onBack,
}: {
  brush: Brush;
  onPick: (b: Brush) => void;
  onBack?: () => void;
}): JSX.Element {
  return (
    <div className="empire-parchment shrink-0 border-b border-panel-edge bg-bg-raised">
      <div className="flex gap-1 overflow-x-auto no-scrollbar px-2 py-2">
        {/* The editor is a place you go INTO from the village, so it needs a
            way back that is not the browser's. */}
        {onBack ? (
          <button
            type="button"
            data-testid="back-to-village"
            onClick={onBack}
            className="h-11 shrink-0 rounded-lg bg-panel px-3 text-xs font-semibold text-ink-dim hover:text-ink"
          >
            ← Village
          </button>
        ) : null}
        {TOOLS.map((tool) => (
          <button
            key={tool.brush}
            type="button"
            title={tool.hint}
            onClick={() => onPick(tool.brush)}
            className={`h-11 whitespace-nowrap rounded-lg px-3.5 text-xs font-semibold transition-colors ${
              brush === tool.brush ? "bg-gold text-bg" : "bg-panel text-ink-dim hover:text-ink"
            }`}
          >
            {tool.label}
          </button>
        ))}
      </div>

      {brush === "select" ? (
        <TowerTray />
      ) : (
        <>
          <Levels onBack={onBack} />
          {brush === "road" ? <LongPressHint /> : null}
        </>
      )}
    </div>
  );
}

/**
 * The five tower types.
 *
 * A type you cannot place is shown greyed with the reason, not hidden: a
 * defender should be able to see that the Cannon exists and costs a Guild 10
 * before they spend anything working towards it.
 */
function TowerTray(): JSX.Element {
  const layout = useEditorStore((s) => s.layout);
  const towerBrush = useEditorStore((s) => s.towerBrush);
  const setTowerBrush = useEditorStore((s) => s.setTowerBrush);

  return (
    <div className="flex gap-1 overflow-x-auto no-scrollbar border-t border-panel-edge px-2 py-2">
      {TOWER_KINDS.map((kind) => {
        const stats = towerStats(kind, 1);
        const blocked = towerUnavailable(layout, kind);
        const armed = towerBrush === kind;
        return (
          <button
            key={kind}
            type="button"
            disabled={blocked !== null}
            title={blocked ?? `${stats.role} · range ${stats.rangeMilli / 1000} tiles`}
            data-testid={`tower-${kind}`}
            onClick={() => setTowerBrush(armed ? null : kind)}
            className={`min-w-[112px] rounded-lg px-3 py-1.5 text-left transition-colors ${
              armed ? "bg-gold text-bg" : "bg-panel text-ink-dim enabled:hover:text-ink"
            } disabled:opacity-35`}
          >
            <span className="flex items-center gap-1.5 text-xs font-semibold">
              <img
                src={`${import.meta.env.BASE_URL}art/portraits/tower-${kind}.webp`}
                width={24}
                height={28}
                className="h-7 w-6 shrink-0 object-contain"
                alt=""
                onError={(e) => {
                  e.currentTarget.style.visibility = "hidden";
                }}
              />
              {stats.name}
            </span>
            <span className="block text-[10px] opacity-80">
              {blocked ?? TOWER_BLURB[kind as TowerKind]}
            </span>
            {blocked ? null : (
              <span className="block text-[10px] tabular-nums opacity-60">
                range {stats.rangeMilli / 1000}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Wall and Engineer's Guild levels — read-only, from the village (D-112).
 *
 * They used to be free steppers here, stored with the maze, which let anyone
 * defend at Guild 20 on day one and made both buildings pointless. The server
 * now takes them from the buildings; the editor says so, and says where the
 * next level comes from.
 */
function Levels({ onBack }: { onBack?: () => void }): JSX.Element {
  const layout = useEditorStore((s) => s.layout);
  const budget = defenceBudget(layout.wallLevel);
  const slots = towerSlots(layout.engineersGuildLevel);

  return (
    <div className="flex items-center gap-3 border-t border-panel-edge px-3 py-1.5 text-[11px] text-ink-dim">
      <span data-testid="level-wall">
        <span className="uppercase tracking-wide">Wall</span>{" "}
        <span className="font-semibold text-ink">{layout.wallLevel}</span>
        <span className="opacity-70"> · {budget.roadTiles} road</span>
      </span>
      <span data-testid="level-guild">
        <span className="uppercase tracking-wide">Guild</span>{" "}
        <span className="font-semibold text-ink">{layout.engineersGuildLevel}</span>
        <span className="opacity-70"> · {slots} towers</span>
      </span>
      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          className="ml-auto h-9 rounded-md bg-panel px-2.5 text-[11px] font-semibold text-ink-dim hover:text-ink"
        >
          Upgrade in village
        </button>
      ) : null}
    </div>
  );
}

/**
 * The one piece of the interface that has to be discoverable.
 *
 * Long-press is now the ONLY way to place mud or a barricade, so this cannot be
 * the line that gets clipped on a narrow screen — which is exactly what it did
 * when it shared a row with the level steppers. It gets its own full-width row,
 * and only while the Road tool is selected, which is the only time it is true.
 */
function LongPressHint(): JSX.Element {
  return (
    <p className="border-t border-panel-edge px-3 py-1.5 text-[11px] text-ink-dim">
      Hold a road tile to turn it into <span className="text-ink">mud</span>, then a{" "}
      <span className="text-ink">barricade</span>.
    </p>
  );
}

/**
 * Floating controls.
 *
 * Every one of these is at least 44px on its shortest side — the size a finger
 * can hit reliably, and what 04 asks for. A 32px button looks tidier in a
 * screenshot and is missed about a third of the time on a phone.
 */
function FloatButton({
  label,
  title,
  onClick,
  disabled,
}: {
  label: string;
  title: string;
  onClick: () => void;
  disabled?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
      className="empire-control h-11 px-3 text-xs disabled:opacity-30"
    >
      {label}
    </button>
  );
}

function HistoryControls(): JSX.Element {
  const undo = useEditorStore((s) => s.undo);
  const redo = useEditorStore((s) => s.redo);
  const resetToDefault = useEditorStore((s) => s.resetToDefault);
  const clearBoard = useEditorStore((s) => s.clearBoard);
  const canUndo = useEditorStore((s) => s.canUndo());
  const canRedo = useEditorStore((s) => s.canRedo());

  return (
    <div className="absolute right-3 top-3 flex gap-1.5">
      <FloatButton label="↶" title="Undo" onClick={undo} disabled={!canUndo} />
      <FloatButton label="↷" title="Redo" onClick={redo} disabled={!canRedo} />
      <FloatButton label="Clear" title="Empty the board and draw your own" onClick={clearBoard} />
      <FloatButton
        label="Default"
        title="Put the starting road and towers back"
        onClick={resetToDefault}
      />
    </div>
  );
}

/**
 * Zoom, and the reason the board looked half-size on a phone.
 *
 * A 12-wide isometric board is twice as wide as it is tall, so fitting it to a
 * portrait screen fills the width and leaves bands of empty space above and
 * below. Fit is for seeing the shape of a maze; you zoom in to build one.
 * Pinch and two-finger drag do the same thing without these buttons.
 */
function ZoomControls({
  view,
  zoom,
}: {
  view: React.RefObject<BoardView | null>;
  zoom: number;
}): JSX.Element {
  const atFit = zoom <= 1.01;
  return (
    <div className="absolute bottom-3 right-3 flex flex-col items-end gap-1.5">
      <span className="rounded bg-black/45 px-2 py-0.5 text-[10px] tabular-nums text-ink-dim">
        {zoom.toFixed(1)}×
      </span>
      <FloatButton label="＋" title="Zoom in" onClick={() => view.current?.zoomBy(1.35)} />
      <FloatButton
        label="Fit"
        title="Fit the whole board"
        onClick={() => view.current?.fitToScreen()}
        disabled={atFit}
      />
      <FloatButton
        label="－"
        title="Zoom out"
        onClick={() => view.current?.zoomBy(1 / 1.35)}
        disabled={atFit}
      />
    </div>
  );
}

/**
 * What the selected tower is, as a small strip over the bottom of the board.
 *
 * It used to open by itself every time a tower went down — a tall card with a
 * table of numbers and no way to dismiss it, covering the part of the board
 * the defender was building on (playtest, 29 Sep 2026). Now it opens only when
 * a built tower is TAPPED, is two lines high, and closes with ✕, a tap on
 * open ground, or picking a tool.
 *
 * Every number here is read from the balance spreadsheet through core-sim —
 * nothing about a tower is typed into the client.
 */
function Inspector(): JSX.Element | null {
  const layout = useEditorStore((s) => s.layout);
  const selected = useEditorStore((s) => s.selected);
  const select = useEditorStore((s) => s.select);
  const removeTower = useEditorStore((s) => s.removeTower);
  const upgradeTower = useEditorStore((s) => s.upgradeTower);
  const playing = useTestStore((s) => s.playing);
  const paid = useSaveStore((s) => s.upgradesPaid);
  const held = useVillageStore(useShallow(predictedResources));
  const [more, setMore] = useState(false);

  // While a replay is running the card is just something covering the board.
  // The range ring stays, because during a battle it is the useful half.
  if (playing) return null;
  if (!selected) return null;
  const tile = layout.tiles[idx(selected.x, selected.y, layout.size)];
  if (tile?.kind !== "tower_slot" || !tile.tower) return null;

  const stats = towerStats(tile.tower.kind, tile.tower.level);
  const level = stats.levels[tile.tower.level - 1]!;
  const perSecond = (level.damage * TICKS_PER_SECOND) / level.cooldownTicks;
  const facts = [
    `range ${stats.rangeMilli / 1000}`,
    `${perSecond.toFixed(1)} dmg/s`,
    stats.splashRadiusMilli > 0 ? `splash ${stats.splashRadiusMilli / 1000}` : null,
    stats.minRangeMilli > 0 ? `min range ${stats.minRangeMilli / 1000}` : null,
    stats.piercesArmor ? "ignores armour" : null,
  ].filter(Boolean);

  // What the whole save would cost with this tower one level up, against what
  // the village holds now. Checked BEFORE the upgrade, so the button is
  // greyed with the reason instead of the Save failing afterwards (29 Sep).
  const upgraded = layout.tiles.map((t, i) =>
    i === idx(selected.x, selected.y, layout.size) && t.tower
      ? { ...t, tower: { ...t.tower, level: t.tower.level + 1 } }
      : t
  );
  const bill = upgradeBill({ ...layout, tiles: upgraded }, paid);
  const missing = RESOURCE_KINDS.filter((k) => bill[k] > held[k]).map(
    (k) => `${bill[k] - held[k]} ${k}`
  );
  const short = missing.length ? `Needs ${missing.join(", ")} more.` : null;

  return (
    <div
      className="absolute bottom-2 left-2 right-2 rounded-xl border border-panel-edge bg-bg-raised/95 px-3 py-2 shadow-lg sm:right-auto sm:w-80"
      data-testid="inspector"
    >
      <div className="flex items-center gap-2">
        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-ink">
          {stats.name}{" "}
          <span className="text-[11px] font-bold text-gold" data-testid="tower-level">
            L{tile.tower.level}
          </span>
          <span className="ml-2 text-[11px] font-normal text-ink-dim">{facts.join(" · ")}</span>
        </span>
        <button
          type="button"
          onClick={() => setMore(!more)}
          className="h-9 shrink-0 rounded bg-panel px-2 text-[11px] font-semibold text-ink-dim"
          data-testid="inspector-more"
        >
          {more ? "Less" : "Upgrade"}
        </button>
        <button
          type="button"
          onClick={() => removeTower(selected)}
          className="h-9 shrink-0 rounded bg-panel px-2 text-[11px] font-semibold text-danger"
        >
          Remove
        </button>
        <button
          type="button"
          aria-label="Close"
          data-testid="inspector-close"
          onClick={() => select(null)}
          className="h-9 w-9 shrink-0 rounded bg-panel text-sm font-bold text-ink-dim"
        >
          ✕
        </button>
      </div>
      {more ? (
        <Upgrade
          kind={tile.tower.kind}
          level={tile.tower.level}
          guild={layout.engineersGuildLevel}
          short={short}
          onUpgrade={() => upgradeTower(selected)}
        />
      ) : null}
    </div>
  );
}

/**
 * Test the maze against three preset armies, free and as often as you like.
 *
 * This is the moment the editor stops being a drawing tool. The same
 * `simulate` the server runs resolves the battle in a worker, so what you see
 * here is not a prediction of the raid — it is the raid.
 */
function TestPanel(): JSX.Element | null {
  const layout = useEditorStore((s) => s.layout);
  const validation = useEditorStore((s) => s.validation);
  const { status, runs, playing, paused, speed, error } = useTestStore();
  const { test, play, togglePause, setSpeed, stop } = useTestStore();

  // Nothing to test until the maze is legal — and the line below already says
  // why it is not.
  if (!validation.ok) return null;

  if (playing) {
    const run = runs.find((r) => r.presetId === playing);
    const preset = PRESETS.find((p) => p.id === playing);
    return (
      <div className="shrink-0 border-t border-panel-edge bg-bg-raised px-3 py-2">
        <div className="flex items-center gap-2 text-xs">
          <span className="font-semibold text-ink">{preset?.name}</span>
          <span className="text-ink-dim">{run ? describe(run.result) : ""}</span>
          <div className="ml-auto flex items-center gap-1">
            <button
              type="button"
              onClick={togglePause}
              className="rounded bg-panel px-2.5 py-1.5 font-semibold text-ink-dim hover:text-ink"
            >
              {paused ? "Play" : "Pause"}
            </button>
            {SPEEDS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSpeed(s)}
                className={`rounded px-2.5 py-1.5 font-semibold ${
                  speed === s ? "bg-gold text-bg" : "bg-panel text-ink-dim hover:text-ink"
                }`}
              >
                {s}×
              </button>
            ))}
            <button
              type="button"
              onClick={stop}
              className="rounded bg-panel px-2.5 py-1.5 font-semibold text-ink-dim hover:text-ink"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="shrink-0 border-t border-panel-edge bg-bg-raised px-3 py-2">
      {status !== "done" ? (
        <div className="flex items-center gap-3">
          <button
            type="button"
            data-testid="test-button"
            disabled={status === "running"}
            onClick={() => void test(layout)}
            className="rounded-lg bg-gold px-4 py-2 text-xs font-bold text-bg disabled:opacity-50"
          >
            {status === "running" ? "Testing…" : "Test this defence"}
          </button>
          <span className="text-[11px] text-ink-dim">
            {error ?? "Three preset armies, same simulation the server runs. Free, unlimited."}
          </span>
        </div>
      ) : (
        <div className="flex gap-2 overflow-x-auto no-scrollbar" data-testid="test-results">
          {PRESETS.map((preset) => {
            const run = runs.find((r) => r.presetId === preset.id);
            if (!run) return null;
            const held = run.result.outcome === "hold";
            return (
              <button
                key={preset.id}
                type="button"
                title={`${preset.blurb} · ${planSize(preset.plan)} units, about ${planCost(
                  preset.plan
                ).toLocaleString()} resources`}
                data-testid={`result-${preset.id}`}
                onClick={() => play(preset.id)}
                className="min-w-[150px] rounded-lg border border-panel-edge bg-panel px-3 py-2 text-left hover:border-gold"
              >
                <span className="flex items-baseline justify-between gap-2">
                  <span className="text-xs font-semibold text-ink">{preset.name}</span>
                  <span
                    className={`text-[10px] font-bold uppercase ${held ? "text-ok" : "text-danger"}`}
                  >
                    {held ? "held" : "breached"}
                  </span>
                </span>
                <span className="mt-0.5 block text-[10px] tabular-nums text-ink-dim">
                  {describe(run.result)}
                </span>
                <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-ink-dim opacity-70">
                  {kindsUsed(preset.plan).map((u) => (
                    <span key={u.kind} className="flex items-center gap-1">
                      <UnitMark kind={u.kind} size={9} />
                      {u.count} {u.kind}
                    </span>
                  ))}
                </span>
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => void test(layout)}
            className="shrink-0 rounded-lg bg-panel px-3 py-2 text-xs font-semibold text-ink-dim hover:text-ink"
          >
            Re-run
          </button>
        </div>
      )}
    </div>
  );
}

function describe(result: {
  army: { reached: number; sent: number; lost: number };
  ticks: number;
}): string {
  const seconds = (result.ticks / TICKS_PER_SECOND).toFixed(1);
  return `${result.army.reached}/${result.army.sent} reached · ${result.army.lost} lost · ${seconds}s`;
}

/**
 * The line under the board. When the layout is illegal it says exactly what the
 * server would say — same function, same words — and the board pulses the
 * tiles at fault.
 */
function Status(): JSX.Element {
  const layout = useEditorStore((s) => s.layout);
  const validation = useEditorStore((s) => s.validation);

  return (
    // shrink-0 matters: without it the flex column squeezes this row and
    // clips the validation message, which is the single most important line
    // on the screen.
    <div
      className="shrink-0 border-t border-panel-edge bg-bg-raised"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <SaveBanner />
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
          <p
            className={`text-sm ${validation.ok ? "text-ok" : "text-danger"}`}
            data-testid="validation"
          >
            {validation.ok ? "This defence is legal." : validation.error.message}
          </p>
          {validation.ok ? null : (
            <p className="text-[11px] text-ink-dim" data-testid="still-defended">
              Not saved. If you leave now, your village keeps its current defence.
            </p>
          )}
          <UpgradeBill />
          <div
            className="flex shrink-0 flex-wrap gap-x-3 gap-y-0.5 text-[11px] tabular-nums"
            data-testid="budget"
          >
            {budgetLines(layout).map((line) => (
              <span
                key={line.label}
                data-testid={`budget-${line.label}`}
                className={
                  line.used > line.allowed
                    ? "text-danger"
                    : line.used === line.allowed
                      ? "text-warn"
                      : "text-ink-dim"
                }
              >
                {line.label} {line.used}/{line.allowed}
              </span>
            ))}
          </div>
        </div>
        <SaveButton />
      </div>
    </div>
  );
}

/** True once the board holds a road or a tower, not just the core. */
function hasMaze(layout: Layout): boolean {
  return layout.tiles.some((t) => t.kind !== "empty" && t.kind !== "core");
}

/**
 * Open the editor zoomed in on the maze, at finger-sized tiles.
 *
 * Fitting the whole 12×12 board on a portrait phone makes each tile about 34 px
 * wide and 17 tall — half a fingertip. On a narrow screen the editor starts at
 * tiles 72 px wide (FINGER_TILE_PX; friends test, Sep 2026: 1.6× fit was
 * still missed by fingers), centred on what is built; the rest is a two-finger
 * drag, a pinch, the Fit button, or painting towards the edge, which scrolls.
 */
function frameEditor(view: BoardView, layout: Layout): void {
  const narrow = view.app.screen.width < 600;
  view.frameAction(1, [layout.gate], 1, narrow ? view.proj.fingerTilePx : 0);
}

/** The kind of tower being dragged to a new spot, if a drag started on one. */
function movingKind(
  layout: Layout,
  stroke: { start: { x: number; y: number } } | null
): TowerKind | undefined {
  if (!stroke) return undefined;
  return layout.tiles[idx(stroke.start.x, stroke.start.y, layout.size)]?.tower?.kind;
}

/**
 * The next level of this tower (D-119): what it does better, what it costs,
 * and — when the Guild is too low — what it is waiting for. Paid on Save.
 */
function Upgrade({
  kind,
  level,
  guild,
  short,
  onUpgrade,
}: {
  kind: TowerKind;
  level: number;
  guild: number;
  /** Why the village cannot pay for it yet, or null. */
  short: string | null;
  onUpgrade: () => void;
}): JSX.Element {
  if (level >= MAX_TOWER_LEVEL) {
    return <p className="mt-2 text-[11px] font-semibold text-gold">Top level.</p>;
  }
  const next = level + 1;
  const now = towerStats(kind, level).levels[level - 1]!;
  const then = towerStats(kind, next).levels[next - 1]!;
  const cost = upgradeCost(kind, next);
  const blocked = towerLevelBlock(next, guild) ?? short;
  return (
    <div className="mt-2 rounded-lg border border-panel-edge bg-panel px-2.5 py-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] text-ink">
          Level {next}: damage {now.damage} → {then.damage}, HP {now.hp} → {then.hp}
        </span>
        <button
          type="button"
          data-testid="tower-upgrade"
          disabled={blocked !== null}
          onClick={onUpgrade}
          className="shrink-0 rounded bg-gold px-2.5 py-1 text-[11px] font-bold text-bg disabled:opacity-40"
        >
          Upgrade
        </button>
      </div>
      <p className="mt-1 flex flex-wrap items-center gap-x-2 text-[10px] tabular-nums text-ink-dim">
        {blocked ? (
          <span className="text-warn">{blocked}</span>
        ) : (
          <>
            <span>Paid when you save:</span>
            {(["wood", "clay", "iron"] as const).map((k) =>
              cost[k] > 0 ? (
                <span key={k} className="flex items-center gap-0.5">
                  <ResourceIcon kind={k} size={10} />
                  {cost[k]}
                </span>
              ) : null
            )}
          </>
        )}
      </p>
    </div>
  );
}
